<template>
  <v-tabs
    v-model="activeTab"
    color="accent"
  >
    <v-tab> Status </v-tab>
    <v-tab> License </v-tab>
    <v-tab> Location </v-tab>
    <v-tab> Installed apps </v-tab>
    <v-tab> Configs </v-tab>
    <v-tab> Wifi Configs </v-tab>
    <v-tab> Events </v-tab>
    <v-tab> Status Logs </v-tab>
    <v-tab> Mytaxi Info </v-tab>
    <v-tab> WiFi management </v-tab>
    <v-tab-item>
      <device-statuses :current-mobile-status="currentMobileStatus" />
    </v-tab-item>
    <v-tab-item>
      <device-licenses :current-mobile-status="currentMobileStatus" />
    </v-tab-item>
    <v-tab-item>
      <device-locations :current-mobile-status="currentMobileStatus" />
    </v-tab-item>
    <v-tab-item>
      <device-apps
        :current-mobile-status="currentMobileStatus"
        :device="device"
      />
    </v-tab-item>
    <v-tab-item>
      <device-configs :device-id="currentMobileStatus" />
    </v-tab-item>
    <v-tab-item>
      <wifi-configs
        :type="2"
        :reference-id="referenceId"
      />
    </v-tab-item>
    <v-tab-item>
      <signal-events :reference-id="referenceId" />
    </v-tab-item>
    <v-tab-item>
      <status-logs :reference-id="referenceId" />
    </v-tab-item>
    <v-tab-item>
      <div class="mytaxi-info-tab">
        <div class="mytaxi-info-tab__actions">
          <app-btn
            :disabled="mytaxiInfoLoading"
            @click="loadMytaxiInfo(true)"
          >
            Refresh Mytaxi info
          </app-btn>
        </div>

        <v-progress-linear
          v-if="mytaxiInfoLoading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <v-alert
          v-if="!mytaxiInfoLoading && mytaxiInfoError"
          dense
          text
          type="warning"
        >
          {{ mytaxiInfoError }}
        </v-alert>

        <div
          v-if="!mytaxiInfoLoading && mytaxiInfoRows.length"
          class="mytaxi-info-scroll"
        >
          <div class="mytaxi-info-grid">
            <div
              v-for="row in mytaxiInfoRows"
              :key="row.label"
              class="mytaxi-info-row"
            >
              <span class="mytaxi-info-row__label">{{ row.label }}</span>
              <span class="mytaxi-info-row__value">{{ row.value }}</span>
            </div>
          </div>
        </div>

        <v-alert
          v-if="!mytaxiInfoLoading && !mytaxiInfoError && !mytaxiInfoRows.length"
          dense
          text
          type="info"
        >
          No Mytaxi info found.
        </v-alert>
      </div>
    </v-tab-item>
    <v-tab-item>
      <wifi-management
        :device-id="referenceId"
        :device-mac-address="device.macAddress"
        :company-id="device.companyId"
        :active="activeTab === 9"
      />
    </v-tab-item>
  </v-tabs>
</template>

<script>
  import DeviceStatuses from './DeviceStatuses.vue'
  import DeviceLicenses from './DeviceLicenses.vue'
  import DeviceApps from './DevicesApps.vue'
  import DeviceLocations from './DeviceLocations.vue'
  import DeviceConfigs from './DeviceConfigs.vue'
  import WifiConfigs from '../WifiConfigs.vue'
  import SignalEvents from '../SignalEvents.vue'
  import StatusLogs from '../StatusLogs.vue'
  import WifiManagement from './WifiManagement.vue'

  export default {
    name: 'DeviceDetailTabs',
    components: {
      DeviceStatuses,
      DeviceLicenses,
      DeviceApps,
      DeviceLocations,
      DeviceConfigs,
      WifiConfigs,
      SignalEvents,
      StatusLogs,
      WifiManagement,
    },
    props: {
      currentMobileStatus: { type: [Number, String], default: 0 },
      device: { type: Object, default: () => ({}) },
      referenceId: { type: [Number, String], default: 0 },
    },
    data: () => ({
      activeTab: 0,
      mytaxiInfo: null,
      mytaxiInfoError: '',
      mytaxiInfoLoading: false,
      mytaxiInfoLoadedMac: '',
    }),
    computed: {
      mytaxiInfoRows () {
        const info = this.mytaxiInfo || {}
        const ownerName = [info.ownerFirstName, info.ownerMiddleName, info.ownerLastName]
          .filter(value => String(value || '').trim())
          .join(' ')
        const rows = [
          ['Device Name', info.name],
          ['Description', info.description],
          ['MAC Address', info.macAddress],
          ['Active', typeof info.active === 'boolean' ? (info.active ? 'Yes' : 'No') : ''],
          ['Driver', ownerName],
          ['Driver Id', info.ownedDriverId],
          ['Driver No', info.ownedDriverNo || info.username],
          ['Driver Phone', info.ownerMobileNumber || info.ownerPhoneNumber],
          ['Vehicle Id', info.vehicleId],
          ['Vehicle', info.vehicleNo],
          ['Address', info.address],
          ['Suburb', info.suburb],
          ['Latitude', info.latitude],
          ['Longitude', info.longitude],
          ['Previous Latitude', info.previousLatitude],
          ['Previous Longitude', info.previousLongitude],
          ['Previous Address', info.previousAddress],
          ['Previous Location Updated', this.formatMytaxiDate(info.previousLocationUpdatedTime)],
          ['Speed', info.speed ? `${info.speed} km/h` : ''],
          ['Location Updated', this.formatMytaxiDate(info.locationUpdatedTime)],
          ['Last Connected', this.formatMytaxiDate(info.lastConnected)],
          ['Device Phone', info.devicePhoneNumber],
          ['Model', info.deviceModel],
          ['OS', info.os],
          ['App Version', info.appVersion],
          ['Tracker', info.trackerMacAddress || info.trackerName || ''],
          ['Tracker Status', info.trackerDisconnected ? 'Disconnected' : ''],
        ]

        return rows
          .filter(([, value]) => String(value || '').trim())
          .map(([label, value]) => ({ label, value }))
      },
    },
    watch: {
      activeTab (value) {
        if (value === 8) {
          this.loadMytaxiInfo()
        }
      },
      device () {
        this.clearMytaxiInfo()
      },
      currentMobileStatus () {
        this.clearMytaxiInfo()
      },
    },
    methods: {
      getMytaxiMacAddress () {
        return String(
          this.device?.macAddress ||
          this.device?.deviceMacAddress ||
          this.device?.deviceId ||
          '',
        ).trim()
      },
      clearMytaxiInfo () {
        this.mytaxiInfo = null
        this.mytaxiInfoError = ''
        this.mytaxiInfoLoading = false
        this.mytaxiInfoLoadedMac = ''

        if (this.activeTab === 8) {
          this.loadMytaxiInfo()
        }
      },
      formatMytaxiDate (value) {
        if (!value) {
          return ''
        }

        return this.$format.dateTime(value)
      },
      async loadMytaxiInfo (force = false) {
        const macAddress = this.getMytaxiMacAddress()

        if (!macAddress) {
          this.mytaxiInfo = null
          this.mytaxiInfoError = 'No Mytaxi info found.'
          return
        }

        if (!force && this.mytaxiInfoLoadedMac === macAddress && (this.mytaxiInfo || this.mytaxiInfoError)) {
          return
        }

        this.mytaxiInfo = null
        this.mytaxiInfoError = ''
        this.mytaxiInfoLoading = true

        try {
          const response = await fetch(
            `https://mcabs.softclient.com.au/devices-api/api/DevicePublic/GetDeviceFromQueue?mac=${encodeURIComponent(macAddress)}`,
          )
          const result = await response.json()
          const data = result?.success === true ? result?.data : null

          if (!data) {
            this.mytaxiInfoError = 'No Mytaxi info found.'
            return
          }

          this.mytaxiInfo = data
          this.mytaxiInfoLoadedMac = macAddress
        } catch (error) {
          this.mytaxiInfoError = 'No Mytaxi info found.'
        } finally {
          this.mytaxiInfoLoading = false
        }
      },
    },
  }
</script>

<style scoped>
.mytaxi-info-tab {
  max-width: calc(100vw - 72px);
  padding: 20px 16px;
  overflow: hidden;
}

.mytaxi-info-tab__actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.mytaxi-info-scroll {
  max-width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  padding-bottom: 14px;
  scrollbar-color: #9de35c #eeeeee;
  scrollbar-gutter: stable;
  width: 100%;
}

.mytaxi-info-scroll::-webkit-scrollbar {
  height: 12px;
}

.mytaxi-info-scroll::-webkit-scrollbar-track {
  background: #eeeeee;
  border-radius: 999px;
}

.mytaxi-info-scroll::-webkit-scrollbar-thumb {
  background: #9de35c;
  border-radius: 999px;
}

.mytaxi-info-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(300px, 1fr));
  gap: 10px 24px;
  min-width: 1680px;
}

.mytaxi-info-row {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.mytaxi-info-row__label {
  color: #455a64;
  font-weight: 700;
}

.mytaxi-info-row__value {
  min-width: 0;
  word-break: break-word;
}
</style>
